/**
 * Modal for spinning up a new tenant app.
 *
 * Opens with a short onboarding carousel (what you'll get once the
 * workspace is created), then lands on the actual creation form as its
 * final step. "Skip" jumps straight to the form.
 *
 * Today only "hr" is offered, but the type is rendered as a list so adding
 * future types is just a matter of pushing into APP_TYPES.
 */

import React, { useState } from 'react';
import Modal from './Modal';
import { useApi } from '../lib/api';
import { Rocket } from 'lucide-react';
import '../styles/CreateAppOnboarding.css';

const APP_TYPES = [
  {
    id: 'hr',
    title: 'Glos workspace',
    description:
      'Set up an organisation with branches, departments, roles, job descriptions, policies, employees, and skills.',
  },
];

const SLIDES = [
  {
    headline: 'Your Personal Workspace',
    support: 'Manage tasks, performance, and hours, all in one central place.',
    image: '/assets/1.png',
  },
  {
    headline: 'Know Your Team & Clients',
    support: 'View organization charts, roles, and track real-time client engagement metrics.',
    image: '/assets/2_team_clients.png',
  },
  {
    headline: 'Stay Connected',
    support: 'Access the company feed to share updates and celebrate milestones together.',
    image: '/assets/2.png',
  },
  {
    headline: 'Track Your Performance',
    support: 'Set goals, receive feedback, and view performance reviews effortlessly.',
    image: '/assets/5_create_app.png',
  },
  {
    headline: 'Ready to Create Your Workspace?',
    support: 'Create personalized workflows and achieve great leaps.',
    cta: true,
  },
];

export default function CreateAppModal({ open, onClose, onCreated }) {
  const api = useApi();
  const [step, setStep] = useState(0);
  const [type, setType] = useState('hr');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const FORM_STEP = SLIDES.length;

  const reset = () => {
    setStep(0);
    setType('hr');
    setName('');
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Please give your workspace a name');
      return;
    }
    if (trimmed.length > 120) {
      setError('Name is too long (max 120 characters)');
      return;
    }
    setSubmitting(true);
    try {
      const created = await api.createApp({ type, name: trimmed });
      onCreated?.(created);
      reset();
      onClose?.();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to create workspace',
      );
      setSubmitting(false);
    }
  };

  const onForm = step === FORM_STEP;
  const slide = onForm ? null : SLIDES[step];
  const imageOnRight = step % 2 === 1;

  return (
    <Modal
      open={open}
      title="Create Workspace"
      onClose={handleClose}
      size="createapp"
      headerExtra={!onForm && !slide.cta ? (
        <button type="button" className="oa-skip" onClick={() => setStep(FORM_STEP)}>
          Skip
        </button>
      ) : null}
    >
      {onForm ? (
        <form onSubmit={handleSubmit} className="create-app-form">
          <label className="form-label">
            What kind of workspace?
            <div className="type-list">
              {APP_TYPES.map((t) => (
                <label
                  key={t.id}
                  className={`type-option ${type === t.id ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="apptype"
                    value={t.id}
                    checked={type === t.id}
                    onChange={() => setType(t.id)}
                  />
                  <div>
                    <div className="type-option-title">{t.title}</div>
                    <div className="type-option-desc">{t.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </label>

          <label className="form-label">
            Name
            <input
              type="text"
              className="form-input"
              placeholder="e.g. Acme Corp HR"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              autoFocus
            />
          </label>

          {error && <div className="form-error">{error}</div>}

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setStep(FORM_STEP - 1)}
              disabled={submitting}
            >
              Back
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create Workspace'}
            </button>
          </div>
        </form>
      ) : (
        <div className="oa-slide">
          {step > 0 && (
            <button
              type="button"
              className="oa-arrow oa-arrow--left"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              aria-label="Previous"
            >
              ‹
            </button>
          )}
          {!slide.cta && (
            <button
              type="button"
              className="oa-arrow oa-arrow--right"
              onClick={() => setStep((s) => s + 1)}
              aria-label="Next"
            >
              ›
            </button>
          )}

          <div className={`oa-horiz${imageOnRight ? ' oa-horiz--reverse' : ''}`}>
            <div className={`oa-illustration${slide.cta ? ' oa-illustration--cta' : ''}`}>
              {slide.cta
                ? <Rocket size={100} className="oa-cta-icon" />
                : <img src={slide.image} alt={slide.headline} className="oa-illustration-img" />}
            </div>
            <div className="oa-text-col">
              <h3 className="oa-headline">{slide.headline}</h3>
              <p className="oa-support">{slide.support}</p>
              {slide.cta && (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => setStep(FORM_STEP)}
                >
                  + Create Workspace
                </button>
              )}
            </div>
          </div>

          {!slide.cta && (
            <div className="oa-dots">
              {SLIDES.filter((s) => !s.cta).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`oa-dot${i === step ? ' active' : ''}`}
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => setStep(i)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
